/**
 * Wall sketch generation + persistence for a floor.
 *
 * Layered so multiple callers share one code path:
 *  - `generateProcessedSkeleton` — pure generation: room polygons + wall-region
 *    envelope → pipeline-processed FloorSkeleton. Abortable; also used for the
 *    WallCreateTool's live preview.
 *  - `generateAndPersistWallSketch` — the annotation choreography: find or
 *    request the floor's WALL_SKETCH annotation, populate it, persist via
 *    `edit_skeleton`, replicate openings/structures.
 *  - `buildLayoutWallSketchForFloor` — the Layout persona bootstrap: derives
 *    rooms + envelope from an existing outline annotation, then runs the two
 *    layers above.
 *
 * The skeleton annotation is created by the existing `request_skeleton`
 * event handler in the React layer. We dispatch the request, wait for the
 * resulting `wall_sketch_added` event for our floor, then populate it.
 */

import * as THREE from 'three';
import { Viewer } from '../../viewer/viewer';
import { PointoramaOutline } from '../PointoramaOutline';
import { PointoramaWallSketch, WallRegionData } from '../PointoramaWallSketch';
import { generateSkeletonFromRooms } from '../../personas/tools/implementations/FloorProcessors';
import { WallRegionComponent } from '../WallRegionBuilder';
import { FloorSkeleton, SkeletonProcessingPipeline } from '../SkeletonProcessingPipeline';
import { replicateOpeningSketchesForFloor } from './replicateOpeningSketches';
import { replicateStructureSketchObjectsForFloor } from './replicateStructureSketchObjects';
import { buildOutlineGraphFromRings, pointInRingXY, type OutlineRingComponent } from './outlineRings';
import { applyWeldedEdgeSizes } from './sdfPartition';
import type { SkeletonEdgeData, SkeletonNodeData } from '@pointorama/potree-shared';

const DEFAULT_EXPANSION_CONFIG = {
  innerWallThickness: 0.15,
  outerWallThickness: 0.3,
  maxRayDistance: 0.5,
  mergeTolerance: 0.05,
};

/** Room polygon input for skeleton generation. */
export interface RoomPolygonData {
  identifier: string;
  polygon: THREE.Vector3[];
}

/**
 * Single shared pipeline instance across every skeleton-generating caller
 * (layout bootstrap, WallCreateTool preview + commit, WallSketchEditorTool
 * regeneration) AND both tools' debug windows. One instance means editing the
 * pass stack in either debug panel affects every generation path — including
 * the create tool's live preview.
 */
let sharedPipeline: SkeletonProcessingPipeline | null = null;
export function getSharedSkeletonPipeline(): SkeletonProcessingPipeline {
  if (!sharedPipeline) {
    sharedPipeline = new SkeletonProcessingPipeline();
    sharedPipeline.loadFromFile();
  }
  return sharedPipeline;
}

/**
 * Generate a pipeline-processed skeleton from room polygons and a
 * precomputed wall-region envelope.
 *
 * Rethrows when `signal` aborts mid-pipeline (callers discard the run);
 * any other pipeline failure logs and falls back to the unprocessed seed.
 */
export async function generateProcessedSkeleton(rooms: RoomPolygonData[], floorHeight: number, wallRegion: WallRegionComponent[], signal?: AbortSignal): Promise<FloorSkeleton> {
  // Pull configurable Create Nodes kwargs from the pipeline so they reach
  // the probe sweep (which runs inside generateSkeletonFromRooms, before
  // the pipeline executes). Matches the WallSketchEditorTool path.
  const pipeline = getSharedSkeletonPipeline();
  await pipeline.waitForLoad();
  const createNodesPass = pipeline.getPassByName('Create Nodes');
  const probeMaxWalk = createNodesPass?.kwargs?.probeMaxWalk;

  // Seed-only generation: produce expandedVertices + roomConnectivity
  // for the pipeline to consume. Cleanup / node creation / edge creation
  // run as configured passes from the shared pipeline JSON.
  const seedResult = generateSkeletonFromRooms(
    rooms,
    floorHeight,
    {
      ...DEFAULT_EXPANSION_CONFIG,
      ...(probeMaxWalk !== undefined ? { probeMaxWalk } : {}),
      skipCleanupPasses: true,
    },
    wallRegion,
  );

  try {
    return await pipeline.process(seedResult.skeleton, signal ? { signal } : undefined);
  } catch (err) {
    if (signal?.aborted) throw err;
    console.error('Wall sketch pipeline failed:', err);
    return seedResult.skeleton;
  }
}

export interface PersistWallSketchOptions {
  /** Rooms the skeleton was generated from (ids go into `request_skeleton`). */
  rooms: RoomPolygonData[];
  /** Wall-region envelope used for generation. */
  wallRegion: WallRegionComponent[];
  /** LEGACY outline annotation to link in the sketch metadata, or null. */
  outlineIdentifier: string | null;
  /** Already-processed skeleton (e.g. a fresh preview result). Generated when omitted. */
  processedSkeleton?: FloorSkeleton;
  /** Wall regions to persist onto the sketch alongside the topology. */
  wallRegions?: WallRegionData[];
  /**
   * Merged outline to write alongside the generated walls: REPLACES the
   * sketch's `role:'OUTLINE'` subgraph with these rings. Omitted → any
   * existing outline subgraph carries over unchanged.
   */
  outlineRings?: OutlineRingComponent[];
}

/**
 * Generate (unless a processed skeleton is supplied) and persist the wall
 * sketch for a floor. If the floor's WALL_SKETCH annotation already exists it
 * is repopulated in place; otherwise one is requested from the React layer
 * and populated when its scene object lands.
 */
export async function generateAndPersistWallSketch(viewer: Viewer, groupIdentifier: string, floorHeight: number, options: PersistWallSketchOptions): Promise<void> {
  const processed = options.processedSkeleton ?? (await generateProcessedSkeleton(options.rooms, floorHeight, options.wallRegion));

  const existing = viewer.scene.wallSketches.find((s) => s.groupIdentifier === groupIdentifier);
  if (existing) {
    populateWallSketch(viewer, existing, processed, options.outlineIdentifier, options.wallRegions, options.outlineRings, floorHeight);
    return;
  }

  // The sketch object arrives through React (annotation created → synced into the scene). When it never
  // does, the generated walls are lost without a trace: say so instead of waiting forever.
  const stillWaiting = setTimeout(() => {
    console.error(
      `generateAndPersistWallSketch: no wall sketch arrived for floor ${groupIdentifier} within 10s — ${processed.edges.size} generated edge(s) were not applied. Sketches in the scene: ${
        viewer.scene.wallSketches.map((s) => `${s.identifier} (floor ${s.groupIdentifier})`).join(', ') || 'none'
      }`,
    );
  }, 10000);
  const onWallSketchAdded = (event: { skeleton?: PointoramaWallSketch }) => {
    const sk = event.skeleton;
    if (!sk || sk.groupIdentifier !== groupIdentifier) return;
    clearTimeout(stillWaiting);
    viewer.scene.removeEventListener('wall_sketch_added', onWallSketchAdded);
    populateWallSketch(viewer, sk, processed, options.outlineIdentifier, options.wallRegions, options.outlineRings, floorHeight);
  };
  viewer.scene.addEventListener('wall_sketch_added', onWallSketchAdded);

  viewer.scene.dispatchEvent({
    type: 'request_skeleton',
    value: {
      floorGroupIdentifier: groupIdentifier,
      floorHeight,
      roomIdentifiers: options.rooms.map((r) => r.identifier),
    },
  });
}

/**
 * Bootstrap a wall skeleton for a floor that already has an outline.
 *
 * Reuses the existing `generateSkeletonFromRooms` algorithm, but feeds it
 * only the rooms whose centroid sits inside the outline rings. Rooms that
 * extend past the outline are ignored — the outline is treated as the
 * source of truth for what counts as "this floor's building footprint".
 *
 * `rings` come from `resolveFloorOutlineRings` (merged outline first, legacy
 * OUTLINE annotation fallback); `outlineIdentifier` is the legacy annotation
 * to link in the sketch metadata, or null when the rings are already merged.
 */
export async function buildLayoutWallSketchForFloor(viewer: Viewer, groupIdentifier: string, floorHeight: number, rings: OutlineRingComponent[], outlineIdentifier: string | null = null): Promise<void> {
  const allRooms = viewer.scene.rooms.filter((room) => {
    if (room.groupIdentifier !== groupIdentifier) return false;
    const polygon = room.getPolygon();
    if (polygon.length === 0) return false;
    const center = centroid(polygon);
    return pointInsideRings(center, rings);
  });

  if (allRooms.length === 0) {
    console.warn(`Layout bootstrap: no rooms inside outline for floor ${groupIdentifier}`);
    return;
  }

  const roomData = allRooms.map((room) => ({
    identifier: room.identifier,
    polygon: room.getPolygon(),
  }));

  // Reuse the existing outline as the wall region envelope instead of
  // re-synthesizing one from the rooms.
  const wallRegion = ringsToWallRegion(rings);

  await generateAndPersistWallSketch(viewer, groupIdentifier, floorHeight, {
    rooms: roomData,
    wallRegion,
    outlineIdentifier,
  });
}

function populateWallSketch(viewer: Viewer, sceneWallSketch: PointoramaWallSketch, generated: FloorSkeleton, outlineIdentifier: string | null, wallRegions?: WallRegionData[], outlineRings?: OutlineRingComponent[], floorHeight = 0): void {
  const nodes: SkeletonNodeData[] = Array.from(generated.nodes.values()).map((node) => ({
    id: node.id,
    name: node.id,
    position: { x: node.position.x, y: node.position.y, z: node.position.z },
    roomIds: node.roomIds,
    edgeIds: node.edgeIds,
    metadata: node.metadata ?? '',
  }));
  const edges: SkeletonEdgeData[] = Array.from(generated.edges.values()).map((edge) => ({
    id: edge.id,
    name: edge.id,
    startNodeId: edge.startNodeId,
    endNodeId: edge.endNodeId,
    roomIds: edge.roomIds,
    leftSize: edge.leftSize ?? 0.1,
    rightSize: edge.rightSize ?? 0.1,
    wallType: edge.wallType ?? ('INNER' as const),
    metadata: edge.metadata ?? '',
  }));

  // The merged outline lives in the SAME graph as a disjoint `role:'OUTLINE'`
  // subgraph — regeneration rebuilds only the WALL edges. Fresh rings (a
  // create-flow commit) replace the subgraph; otherwise the existing outline
  // nodes/edges carry over verbatim (ids preserved).
  if (outlineRings && outlineRings.length > 0) {
    const outlineGraph = buildOutlineGraphFromRings(outlineRings, floorHeight);
    nodes.push(...outlineGraph.nodes);
    edges.push(...outlineGraph.edges);
  } else {
    appendExistingOutlineSubgraph(sceneWallSketch, nodes, edges);
  }

  // The skin's first run welds connected near-equal wall thicknesses to one
  // width (pass-through junctions) — persist that weld into the edge data so
  // the harmonized width is authoritative, not just cosmetic.
  const weldedEdges = applyWeldedEdgeSizes(nodes, edges);

  // Stamp the originating outline id into the skeleton's metadata so
  // a later regenerate (via WallSketchEditorTool or another bootstrap)
  // reuses the same wall-region envelope instead of synthesizing a new
  // one from the rooms.
  const metadata = sceneWallSketch.setOutlineIdentifier(outlineIdentifier);
  sceneWallSketch.setData(nodes, weldedEdges, metadata);
  if (wallRegions) sceneWallSketch.setWallRegions(wallRegions);

  // Persist via the existing `edit_skeleton` channel — same path
  // WallSketchEditorTool uses. Top-level `metadata` carries the outline
  // linkage; the hook writes it to `getWallSketchMetadataProperty`.
  viewer.scene.dispatchEvent({
    type: 'edit_skeleton',
    value: {
      annotationIdentifier: sceneWallSketch.identifier,
      nodes,
      edges: weldedEdges,
      metadata,
    },
  });

  // Floor→sketch conversion: transform the floor's floorplan openings and
  // structures (columns/beams) into their sketch equivalents (OPENING_SKETCH /
  // SKETCH_OBJECT) on the freshly-populated wall sketch. Both are idempotent
  // (keyed on their source-id properties) — re-running bootstrap never
  // duplicates. The originals are kept for the floorplan persona.
  replicateOpeningSketchesForFloor(viewer, sceneWallSketch);
  replicateStructureSketchObjectsForFloor(viewer, sceneWallSketch);
}

/**
 * Convert an outline annotation's rings into the `WallRegionComponent`
 * shape that `generateSkeletonFromRooms` expects via the
 * `precomputedWallRegion` parameter. Outline rings follow polygon-
 * clipping's outer-CCW / hole-CW convention (OutlineTool persists them
 * straight from `buildWallRegionFromRooms`); `generateSkeletonFromRooms`
 * re-normalizes windings defensively, so a stray inversion still gets
 * corrected.
 */
export function outlineToWallRegion(outline: PointoramaOutline): WallRegionComponent[] {
  return [
    {
      outerRing: outline.getOuterRing().map((p) => new THREE.Vector2(p.x, p.y)),
      holes: outline.getHoles().map((h) => h.map((p) => new THREE.Vector2(p.x, p.y))),
    },
  ];
}

/** Rings-based variant of {@link outlineToWallRegion} — the resolver-driven
 *  callers (merged outline first, legacy fallback) use this. */
export function ringsToWallRegion(rings: readonly OutlineRingComponent[]): WallRegionComponent[] {
  return rings.map((component) => ({
    outerRing: component.outerRing.map((p) => new THREE.Vector2(p.x, p.y)),
    holes: component.holes.map((h) => h.map((p) => new THREE.Vector2(p.x, p.y))),
  }));
}

/** Carry a sketch's existing `role:'OUTLINE'` subgraph over into freshly
 *  generated node/edge arrays (regeneration only rebuilds the walls). Every
 *  regenerate path (bootstrap, WallCreateTool commit, editor pipeline rerun)
 *  must call this before `setData` or the merged outline would be wiped. */
export function appendExistingOutlineSubgraph(sceneWallSketch: PointoramaWallSketch, nodes: SkeletonNodeData[], edges: SkeletonEdgeData[]): void {
  const outlineEdges = sceneWallSketch.getOutlineEdges();
  if (outlineEdges.length === 0) return;
  const outlineNodeIds = new Set<string>();
  for (const e of outlineEdges) {
    outlineNodeIds.add(e.startNodeId);
    outlineNodeIds.add(e.endNodeId);
  }
  const generatedNodeIds = new Set(nodes.map((n) => n.id));
  const generatedEdgeIds = new Set(edges.map((e) => e.id));
  for (const n of sceneWallSketch.getNodes()) {
    if (outlineNodeIds.has(n.id) && !generatedNodeIds.has(n.id)) nodes.push(n);
  }
  for (const e of outlineEdges) {
    if (!generatedEdgeIds.has(e.id)) edges.push(e);
  }
}

/**
 * Convert-on-first-edit: fold a floor's legacy OUTLINE annotation(s) into its
 * wall sketch as `role:'OUTLINE'` ring edges. Runs only when the floor is in
 * the MEASURED representation, has legacy outline rings, and its sketch
 * doesn't already carry merged ones. The legacy annotations are NOT deleted —
 * they become hidden tombstones (their ids recorded in the sketch metadata);
 * undoing the conversion re-shows them automatically. Returns true when a
 * conversion was kicked off.
 */
export function maybeConvertLegacyOutline(viewer: Viewer, floorGroupId: string, floorHeight: number, opts?: { onBeforeConvert?: () => void }): boolean {
  if (!(viewer.floorplanRepresentation?.isOriginalActive(floorGroupId) ?? false)) return false;
  const sketch = viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorGroupId);
  if (sketch && sketch.getOutlineEdges().length > 0) return false; // already merged

  const legacyOutlines = viewer.scene.outlines.filter((o) => o.groupIdentifier === floorGroupId);
  const rings: OutlineRingComponent[] = [];
  for (const outline of legacyOutlines) {
    const outerRing = outline.getOuterRing().map((p) => ({ x: p.x, y: p.y, z: p.z }));
    if (outerRing.length < 3) continue;
    rings.push({ outerRing, holes: outline.getHoles().map((h) => h.map((p) => ({ x: p.x, y: p.y, z: p.z }))) });
  }
  if (rings.length === 0) return false;

  // The conversion is definitely happening — let the caller snapshot undo etc.
  opts?.onBeforeConvert?.();
  writeOutlineRingsToSketch(viewer, floorGroupId, floorHeight, rings, { convertedOutlineIds: legacyOutlines.map((o) => o.identifier) });
  return true;
}

/**
 * Replace a floor's merged-outline subgraph with `rings`, keeping every wall
 * edge. Finds the floor's WALL_SKETCH scene object, or requests an empty one
 * via `request_skeleton` and populates it when it lands. Persists through the
 * normal `edit_skeleton` channel (one undoable step). Used by legacy-outline
 * conversion and outline creation flows.
 */
export function writeOutlineRingsToSketch(viewer: Viewer, floorGroupId: string, floorHeight: number, rings: readonly OutlineRingComponent[], opts?: { convertedOutlineIds?: string[] }): void {
  const apply = (sketch: PointoramaWallSketch): void => {
    const outlineGraph = buildOutlineGraphFromRings(rings, floorHeight);
    const keptEdges = sketch.getEdges(); // wall edges only — the outline subgraph is being replaced
    const keptEdgeIds = new Set(keptEdges.map((e) => e.id));
    // Drop nodes that only supported the old outline subgraph.
    const keptNodes = sketch.getNodes().filter((n) => n.edgeIds.some((id) => keptEdgeIds.has(id)) || n.edgeIds.length === 0);
    const nodes = [...keptNodes, ...outlineGraph.nodes];
    const edges = [...keptEdges, ...outlineGraph.edges];

    const metadata = opts?.convertedOutlineIds?.length ? sketch.setConvertedOutlineIds(opts.convertedOutlineIds) : sketch.getMetadata();
    sketch.setData(nodes, edges, metadata);
    viewer.scene.dispatchEvent({
      type: 'edit_skeleton',
      value: { annotationIdentifier: sketch.identifier, nodes, edges, metadata },
    });
  };

  const existing = viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorGroupId);
  if (existing) {
    apply(existing);
    return;
  }
  const onAdded = (event: { skeleton?: PointoramaWallSketch }) => {
    const sk = event.skeleton;
    if (!sk || sk.groupIdentifier !== floorGroupId) return;
    viewer.scene.removeEventListener('wall_sketch_added', onAdded);
    apply(sk);
  };
  viewer.scene.addEventListener('wall_sketch_added', onAdded);
  viewer.scene.dispatchEvent({
    type: 'request_skeleton',
    value: { floorGroupIdentifier: floorGroupId, floorHeight, roomIdentifiers: [] },
  });
}

function centroid(polygon: THREE.Vector3[]): THREE.Vector2 {
  let sx = 0;
  let sy = 0;
  for (const p of polygon) {
    sx += p.x;
    sy += p.y;
  }
  return new THREE.Vector2(sx / polygon.length, sy / polygon.length);
}

/** Inside any component's outer ring but not in one of its holes. */
function pointInsideRings(point: THREE.Vector2, rings: readonly OutlineRingComponent[]): boolean {
  for (const component of rings) {
    if (!pointInRingXY(point.x, point.y, component.outerRing)) continue;
    if (component.holes.some((hole) => pointInRingXY(point.x, point.y, hole))) continue;
    return true;
  }
  return false;
}
